import { ArrowLeftCircleIcon, ArrowRightCircleIcon, DocumentTextIcon, MapIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setLoanDetail, setLoanGroupData } from 'actions'
import { LoanDefaultsResponse } from 'components/LoanDefaults'
import { PhoneLink } from 'components/PhoneLink'
import {
  convertValueToLabel,
  FieldOrigin,
  fieldsByTransactionTypeAndProductTypeConstant,
  getMappedProductType,
  getProductTypeLabel,
  IS_PRODUCTION,
  IVisibleProp,
  ProductTypes,
} from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { ILoan } from 'pages/LoanStructure/interfaces'
import {
  analyzeLoanStructureValue,
  calcInitialLoanAmount,
  calculateLoanLimits,
  calculateLTV,
  estimatedClosingAmountBorrower,
  isNonDSCRProduct,
} from 'pages/LoanStructure/Logic'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { loanTypeMap } from 'reducers/loanDetail.reducer'
import {
  getAdminConfig,
  getServicingDefaults,
  getServicingNotes,
  getTierLevelList,
  updateLoanFields,
  updateLoanProcess,
} from 'services'
import { combinedMonthlyExpense } from 'services/pdfs/utils'
import { Input, Select, Tooltip } from 'stories/components'
import {
  calculateDTI,
  formatDate,
  formatDateYMD,
  getOpenLoanDisplayNo,
  getPrice2decimal,
  getPrice3decimal,
  isNull,
  removeComma,
  showDistances,
} from 'utils'
import { getInterestOrNot } from 'utils/getInterestOrNot'

import { AllowCashOutType, AllowCashOutTypeText } from '../../../pages/LoanSubmission/types'
import {
  getLoanStructureFieldKey,
  getReportPlaceholderKey,
  LoanDetailsFieldConfig,
  normalizeLoanDetailsFieldConfig,
} from './loanDetails.constants'
import { floaterPanelEdgeClass, floaterTabEdgeClass, FloaterTabWrapper, useFloaterTab } from './useFloaterTab'

export const LoanDetailsSlide = () => {
  const [isShowSlide, setShowSlide] = useState(false)
  const [tierLevelList, setTierLevelList] = useState<any[]>([])
  const [servicingInfo, setServicingInfo] = useState<Record<string, any>>({})
  const [servicingDefaults, setServicingDefaults] = useState<LoanDefaultsResponse | null>(null)

  const floater = useFloaterTab('loanDetail', { side: 'left', percent: 20, mobileTop: 136 })
  const [fieldConfig, setFieldConfig] = useState<LoanDetailsFieldConfig>(() => normalizeLoanDetailsFieldConfig(null))

  const dispatch = useDispatch()
  const { hasPermission } = usePermissions()

  const canEditLoanDetailsSideBar = hasPermission('Loan.FloatTab.Details.Edit')

  const { borrower, loan, loanDetail, fieldProperties } = useSelector((state: any) => ({
    borrower: state.borrower,
    loan: state.loan as ILoan,
    loanDetail: state.loanDetail,
    fieldProperties: ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[FieldOrigin.LoanStructure] ||
      {}) as Record<string, IVisibleProp>,
  }))
  const reportValues: Record<string, any> = loanDetail?.loanDetailsReportValues || {}
  const { proposedLoanAmount } = loan
  const { constructionReserve, rehabBudget, propertyPurchasePrice, interestReserve, lienPayoff, afterRepairValue } =
    analyzeLoanStructureValue(loan)

  let assetTotal = 0
  const includeCoborrower = !!(loanDetail?.includeCoborrower ?? loan?.includeCoborrower)
  Object.keys(borrower).forEach((boKey) => {
    const isPrimary = boKey === 'borrower'
    // Match credit-score / A&L: ignore co-borrower assets when coborrower is off the loan.
    if (!(isPrimary || (includeCoborrower && borrower[boKey]?.isIncluded !== false))) return
    borrower[boKey]?.assets?.forEach((item: any) => {
      assetTotal += removeComma(item.balance)
    })
  })

  useEffect(() => {
    getAdminConfig('floaterTabs').then((res) => {
      setFieldConfig(normalizeLoanDetailsFieldConfig(res?.loanDetail?.fieldConfig))
    })
  }, [])

  useEffect(() => {
    const configuredFields = new Set(fieldConfig.sections.flatMap((section) => section.fields))
    const servicingDates = ['currentMaturityDate', 'nextPaymentDate', 'paymentDaysLate']
    if (!servicingDates.some((key) => configuredFields.has(key))) return

    let cancelled = false
    getServicingNotes()
      .then((res) => {
        if (!cancelled) setServicingInfo(res?.data || {})
      })
      .catch(() => {
        if (!cancelled) setServicingInfo({})
      })
    return () => {
      cancelled = true
    }
  }, [fieldConfig])

  /**
   * How late the loan is on its payment, read from the service rather than worked out here so this
   * panel and the Days Late pipeline column can never disagree. Calendar days from the Servicing
   * Information next payment due date to today, with nothing subtracted for grace.
   */
  useEffect(() => {
    const configuredFields = new Set(fieldConfig.sections.flatMap((section) => section.fields))
    if (!configuredFields.has('paymentDaysLate')) return

    let cancelled = false
    getServicingDefaults()
      .then((res) => {
        if (!cancelled) setServicingDefaults(res || null)
      })
      .catch(() => {
        if (!cancelled) setServicingDefaults(null)
      })
    return () => {
      cancelled = true
    }
  }, [fieldConfig])

  useEffect(() => {
    if (loanDetail.pricingEngine) {
      const programId = loanDetail.rateData?.programId
      if (!programId) {
        setTierLevelList([])
        return
      }

      let cancelled = false
      getTierLevelList(programId)
        .then((res) => {
          if (!cancelled) setTierLevelList(Array.isArray(res) ? res : [])
        })
        .catch(() => {
          if (!cancelled) setTierLevelList([])
        })
      return () => {
        cancelled = true
      }
    }
  }, [loanDetail.rateData?.programId])

  const { values, labelOverrides } = useMemo(() => {
    const bo = borrower?.borrower || {}
    const pitis = combinedMonthlyExpense(loan, loanDetail.rateData)
    const dtiResult = calculateDTI({
      loan,
      borrowerState: borrower,
      proposedPITIA: removeComma(pitis.sum),
      presentPITIA: removeComma(pitis.proposedMonthlyRent),
    })
    const isPrimary = ['Owner Occupied', 'Partially Owner Occupied'].includes(loan.proposedOccupancy)
    const frontLabel = isPrimary ? 'Proposed PITIA' : 'Present Housing Expense'
    const frontTooltip =
      `${frontLabel}: ${getPrice2decimal(isPrimary ? dtiResult.proposedPITIA : dtiResult.presentPITIA)}<br/>` +
      `Total Qualifying Income: ${getPrice2decimal(dtiResult.totalQualifyingIncome)}<br/><br/>` +
      `Front-End DTI = ${frontLabel} / Total Income`
    const backTooltip =
      `${frontLabel}: ${getPrice2decimal(isPrimary ? dtiResult.proposedPITIA : dtiResult.presentPITIA)}<br/>` +
      (isPrimary ? '' : `Negative Subject Cash Flow: ${getPrice2decimal(dtiResult.negativeSubjectCashFlow)}<br/>`) +
      `Net REO Loss: ${getPrice2decimal(dtiResult.netREOLoss)}<br/>` +
      `Included Monthly Liabilities: ${getPrice2decimal(dtiResult.includedMonthlyLiabilities)}<br/>` +
      `Total Monthly Debt: ${getPrice2decimal(dtiResult.totalMonthlyDebt)}<br/>` +
      `Total Qualifying Income: ${getPrice2decimal(dtiResult.totalQualifyingIncome)}<br/><br/>` +
      `Back-End DTI = Total Monthly Debt / Total Income`
    const ltvs = calculateLoanLimits(loan)
    const { value: cashToCloseBorrower, label: cashToCloseBorrowerLabel } = estimatedClosingAmountBorrower(
      loan,
      loanDetail.loanGlobalSettings,
    )
    const isRefinance = loan.transactionType === 'Refinance'
    const isCashFromBorrower = cashToCloseBorrowerLabel.includes('from')
    const productTypeForRules = getMappedProductType(loan.productType, fieldProperties) as ProductTypes
    const isNonDSCR = isNonDSCRProduct(loan.productType, fieldProperties)
    const isBridge = productTypeForRules === ProductTypes.Bridge

    const maxCash = removeComma(assetTotal) - removeComma(pitis['requiredReserves'])
    let maxCashLabel = `$ ${getPrice2decimal(maxCash, false, true)}`
    if (maxCash < 0) maxCashLabel = `$ 0.00`
    const excessCash = Math.abs(cashToCloseBorrower) - removeComma(pitis['requiredReserves'])
    let excessCashLabel = `$ ${getPrice2decimal(excessCash, false, true)}`
    if (excessCash < 0) excessCashLabel = `$ 0.00`
    const totalRequiredAssets =
      removeComma(pitis['requiredReserves']) + (isCashFromBorrower ? Math.abs(cashToCloseBorrower) : 0)
    let totalRequiredAssetsLabel = `$ ${getPrice2decimal(totalRequiredAssets, false, true)}`
    if (totalRequiredAssets < 0) totalRequiredAssetsLabel = `$ 0.00`

    const showPurchasePrice =
      fieldsByTransactionTypeAndProductTypeConstant[getMappedProductType(loan.productType, fieldProperties) as ProductTypes]?.[
        loan.transactionType
      ]?.indexOf('propertyPurchasePrice') !== -1

    const rateData = loanDetail.rateData
    const rawTierLevel = rateData?.tierLevel
    const hasTierValue = rawTierLevel !== '' && rawTierLevel != null && Number.isFinite(Number(rawTierLevel))
    const showTierSelect = canEditLoanDetailsSideBar && loanDetail.pricingEngine
    const tierLevelValue = loanDetail.pricingEngine && hasTierValue ? rawTierLevel : 'N/A'
    const currentMaturityDate = servicingInfo?.curMatDate || ''
    const nextPaymentDate = servicingInfo?.nextPaymentDate || ''
    const today = formatDateYMD()
    const normalizedNextPaymentDate = nextPaymentDate ? formatDateYMD(nextPaymentDate) : ''
    const isDelinquent = !!normalizedNextPaymentDate && normalizedNextPaymentDate < today
    // Same treatment as a past-due payment, for the same reason: these are the two dates somebody
    // working a problem loan scans the panel for, and a date that has simply gone by reads as
    // ordinary text otherwise. Elapsed, not in-default - grace and the balance decide whether the
    // loan badges as a default, but a maturity date in the past is worth seeing either way.
    const normalizedMaturityDate = currentMaturityDate ? formatDateYMD(currentMaturityDate) : ''
    const isMatured = !!normalizedMaturityDate && normalizedMaturityDate < today
    const daysPastDue = servicingDefaults?.daysPastDue
    const paymentDefault = (servicingDefaults?.defaults || []).find((item) => item.type === 'payment')

    const values: Record<string, React.ReactNode> = {
      firstName: bo.firstName,
      lastName: bo.lastName,
      tierLevel: showTierSelect ? (
        <Select
          id="tier-level"
          title=""
          size={3}
          className="-mb-4"
          options={tierLevelList}
          value={hasTierValue ? String(rawTierLevel) : ''}
          onChange={(v) => onFieldChange('tierLevel', v)}
          hasDefaultOption
          defaultOptionText="N/A"
        />
      ) : (
        tierLevelValue
      ),
      entityName: bo.hasEntityTitle ? bo.entityTitle : 'N/A',
      entityState: bo.entityState,
      borrowerType: bo.borrowerType,
      ssn: bo.ssn,
      dob: formatDate(bo.dob),
      creditScore: loan.estimatedCreditScore,
      nonPersonEntity: bo.hasEntityTitle ? 'Yes' : 'No',
      street: (
        <div className="flex items-center gap-2">
          {bo.presentAddress}
          <Tooltip placement="right" message="Show Distance">
            <span
              className="btn-icon"
              onClick={() =>
                showDistances('Distance to Subject Property Address', {
                  addresses: [bo.presentAddress, loan.subjectPropertyAddress],
                })
              }
            >
              <MapIcon className="w-4 h-4" />
            </span>
          </Tooltip>
        </div>
      ),
      citizenship: loan.residency,
      firstTimeHomeBuyer: loan.firstTimeHomeBuyer ? 'Yes' : 'No',
      maritalStatus: bo.maritalStatus,
      preferredLanguage: bo.preferredLanguage,
      workPhone: <PhoneLink value={bo.phone} />,
      email: bo.email,
      propertyAddress: loan.subjectPropertyAddress,
      propertyType: loan.propertyType,
      numberOfUnits: loan.numberOfUnits,
      occupancyType: loan.proposedOccupancy,
      loanPurpose: loan.transactionType,
      loanProgramName: loanDetail.rateData?.program,
      amortizationType: loan.amortizationType,
      loanTerm: `${loan.loanTerm} months`,
      interestRate: loanDetail.rateData?.rate,
      interestOnly: getInterestOrNot(
        loanDetail.rateData?.type || loan.amortizationType,
        loanDetail.rateData?.interestOnly,
      )
        ? 'Yes'
        : 'No',
      productType: getProductTypeLabel(loan.productType, fieldProperties),
      baseLoan: `$ ${getPrice3decimal(proposedLoanAmount)}`,
      loanWith: `$ ${getPrice3decimal(proposedLoanAmount)}`,
      initialLoanAmount: `$ ${getPrice3decimal(calcInitialLoanAmount(loan))}`,
      asIsAppraisedValue: `$ ${getPrice3decimal(loan.asIsValue)}`,
      mortgagePayment: `$ ${getPrice3decimal(pitis['mortgagePayment'])}`,
      ltv: `${calculateLTV()} %`,
      cltv: `${calculateLTV(null, true)} %`,
      requiredReserves: `$ ${getPrice2decimal(pitis['requiredReserves'], false, true)}`,
      totalRequiredAssets: totalRequiredAssetsLabel,
      assets: `$ ${getPrice3decimal(assetTotal)}`,
      maxAllowableCashToClose: maxCashLabel,
      allowCashOut: 'ALLOW_CASHOUT',
      cashToClose: `$ ${getPrice3decimal(Math.abs(cashToCloseBorrower).toFixed(2))}`,
      originationChannel: loanTypeMap[loanDetail.loanType],
      escrowWaiver: isNonDSCR ? 'Waive Both' : loan.escrowType,
      firstTimeHomeBuyerOther: loan.firstTimeHomeBuyer ? 'Yes' : 'No',
      // Keep the row visible when unlocked — empty value, not undefined (undefined is filtered out).
      lockExpiration: loanDetail.rateData?.expireDate ? formatDate(loanDetail.rateData.expireDate) : '',
      // The assigned loan officer, or the name a migrated loan carries when the system it came
      // from had no user to assign - resolved server-side so this agrees with the pipeline's L.O
      // column. Empty string, not undefined, so the row stays visible when nobody is assigned.
      loanOfficer: loanDetail.parties.loanOfficer?.name || '',
      brokerCompanyName: loanDetail.parties.broker?.companyName,
      brokerStateOfFormation: loanDetail.parties.broker?.companyStateOfFormation,
      brokerCompanyType: loanDetail.parties.broker?.companyType,
      closingDate: formatDate(loanDetail.closingDate),
      currentMaturityDate: currentMaturityDate ? (
        <span className={isMatured ? 'font-semibold text-red-600' : ''}>{formatDate(currentMaturityDate)}</span>
      ) : (
        ''
      ),
      nextPaymentDate: nextPaymentDate ? (
        <span className={isDelinquent ? 'font-semibold text-red-600' : ''}>{formatDate(nextPaymentDate)}</span>
      ) : (
        ''
      ),
      // Blank only when the loan has no due date on file, so "current" stays distinguishable from
      // "nothing to be late against". The tooltip names the due date the count runs from, and says
      // when the loan is past due without yet being in default - a figure sitting beside a date
      // already printed in red otherwise invites the question.
      paymentDaysLate:
        daysPastDue === null || daysPastDue === undefined ? (
          ''
        ) : (
          <Tooltip
            message={
              daysPastDue > 0
                ? `Payment due ${formatDate(nextPaymentDate)}: ${daysPastDue} day${
                    daysPastDue === 1 ? '' : 's'
                  } past due.${
                    paymentDefault ? '' : ' Not yet in default - still inside its grace period, or the balance is paid.'
                  }`
                : `Not past due. The next payment is due ${formatDate(nextPaymentDate)}.`
            }
          >
            <span className={daysPastDue > 0 ? 'font-semibold text-red-600' : ''}>{daysPastDue}</span>
          </Tooltip>
        ),
    }

    if (!IS_PRODUCTION && productTypeForRules == ProductTypes.NonQM) {
      values.frontEndDTI = (
        <span className="inline-flex items-center gap-1">
          {isNull(dtiResult.firstDTI) ? 'N/A' : `${dtiResult.firstDTI}%`}
          <Tooltip message={frontTooltip} />
        </span>
      )
      values.backEndDTI = (
        <span className="inline-flex items-center gap-1">
          {isNull(dtiResult.secondDTI) ? 'N/A' : `${dtiResult.secondDTI}%`}
          <Tooltip message={backTooltip} />
        </span>
      )
    }

    if (showPurchasePrice) {
      values.purchasePrice = `$ ${getPrice3decimal(propertyPurchasePrice)}`
    }
    if (isNonDSCR) {
      values.interestReserve = `$ ${getPrice3decimal(interestReserve)}`
      if (!isBridge) {
        values.constructionBudget = `$ ${getPrice3decimal(rehabBudget)}`
        values.constructionAmountFinanced = `$ ${getPrice3decimal(constructionReserve)}`
      }
    }
    if (!isNonDSCR || isBridge) {
      // The loan's own figure where it has one, else what the trust sub-ledger was funded with,
      // resolved server-side (`subLedgerCommitment`). The TMO conversion writes the holdback and
      // its draws to that ledger and leaves `loan.escrowRepairHoldback` null - null on all but 1
      // of longhorn's 15,231 serviced loans - so this row read "$ 0.000" on every converted loan
      // holding repair money. Read-only: it is deliberately not the editable loan field.
      values.escrowRepairHoldback = `$ ${getPrice3decimal(
        removeComma(loan.escrowRepairHoldback) || removeComma(loanDetail.escrowRepairHoldbackFunded ?? 0),
      )}`
    }
    if ([ProductTypes.GroundUpConstruction, ProductTypes.FixAndFlip].includes(productTypeForRules)) {
      values.afterRepairedAppraisedValue = `$ ${getPrice3decimal(loan.afterRepairValue)}`
    }
    if (
      ![ProductTypes.Bridge, ProductTypes.DSCR, ProductTypes.NonQM].includes(productTypeForRules) &&
      ltvs.arv_ltv !== undefined
    ) {
      values.arvLtv = `${ltvs.arv_ltv} %`
    }
    if (isNonDSCR && ltvs.ltc !== undefined) {
      values.ltc = `${ltvs.ltc} %`
    }
    if (isNonDSCR) {
      values.trueLtc =
        afterRepairValue > 0 ? `${((100 * (lienPayoff + rehabBudget)) / afterRepairValue).toFixed(3)} %` : '-'
    }
    if (isNonDSCR && !isRefinance && ltvs.ltp !== undefined) {
      values.ltp = `${ltvs.ltp} %`
    }
    if (!isNonDSCR) {
      values.dscr = pitis['dscr']
      values.prepaymentPenalty = `${convertValueToLabel('prepaymentPenalty', loan.prepaymentPenalty, fieldProperties)}`
    }
    if (isRefinance) {
      values.cashToBorrowerExcess = excessCashLabel
    }
    if (
      [AllowCashOutType.YES_WITH_REQUIREMENT, AllowCashOutType.YES_WITH_RESTRICTION].includes(
        loan.allowCashOut?.toString() as AllowCashOutType,
      )
    ) {
      values.allowCashOutPercentage = 'PERCENTAGE'
    }

    const labelOverrides: Record<string, string> = {
      mortgagePayment: pitis['title'],
      cashToClose: cashToCloseBorrowerLabel,
    }

    return { values, labelOverrides }
  }, [
    borrower,
    loan,
    loanDetail,
    fieldProperties,
    assetTotal,
    proposedLoanAmount,
    constructionReserve,
    rehabBudget,
    propertyPurchasePrice,
    interestReserve,
    lienPayoff,
    afterRepairValue,
    tierLevelList,
    canEditLoanDetailsSideBar,
    servicingInfo,
    servicingDefaults,
  ])

  const displaySections = useMemo(() => {
    // customInformation holds custom Loan Structure fields; not typed on ILoan but present at runtime
    const loanRecord = loan as ILoan & { customInformation?: Record<string, any> }
    const loanWithCustom: Record<string, any> = {
      ...loanRecord,
      ...(loanRecord.customInformation || {}),
    }

    const formatLoanFieldValue = (fieldKey: string, raw: any): React.ReactNode => {
      if (raw === undefined || raw === null || raw === '') return ''
      const prop = fieldProperties[fieldKey]
      const inputType = String(prop?.inputType || '').toLowerCase()

      if (
        ['togglebutton', 'checkbox', 'check', 'ToggleButton', 'CheckBox'].includes(prop?.inputType as string) ||
        inputType === 'togglebutton' ||
        inputType === 'checkbox' ||
        inputType === 'check'
      ) {
        return !!raw ? 'Yes' : 'No'
      }

      const labeled = convertValueToLabel(fieldKey, raw, fieldProperties)
      if (labeled !== undefined && labeled !== null && labeled !== String(raw)) return labeled

      if (['dollar', 'thousandsepwithdoller', 'thousandsep'].includes(inputType) || inputType.includes('dollar')) {
        return `$ ${getPrice3decimal(raw)}`
      }
      if (inputType === 'percentage' || inputType.includes('percent')) {
        return `${raw}%`
      }
      if (inputType === 'date') {
        return formatDate(raw)
      }
      if (typeof raw === 'boolean') return raw ? 'Yes' : 'No'
      return labeled || String(raw)
    }

    return fieldConfig.sections
      .map((section) => {
        const fields = section.fields
          .filter((key) => fieldConfig.fields[key]?.visible !== false)
          .map((key) => {
            const def = fieldConfig.fields[key]
            const reportKey = getReportPlaceholderKey(key, def)
            const loanFieldKey = getLoanStructureFieldKey(key, def)

            let value: React.ReactNode
            if (reportKey) {
              value =
                reportValues[reportKey] === undefined || reportValues[reportKey] === null
                  ? ''
                  : String(reportValues[reportKey])
            } else if (loanFieldKey) {
              value = formatLoanFieldValue(loanFieldKey, loanWithCustom[loanFieldKey])
            } else {
              value = values[key]
            }

            if (value === undefined || value === null) return null
            return {
              key,
              title: labelOverrides[key] || def?.title || key,
              value,
            }
          })
          .filter(Boolean) as Array<{ key: string; title: string; value: React.ReactNode }>
        return { ...section, fields }
      })
      .filter((section) => section.fields.length > 0)
  }, [fieldConfig, values, labelOverrides, reportValues, loan, fieldProperties])

  const onShowSlide = () => {
    setShowSlide(!isShowSlide)
  }

  const onFieldChange = (key: string, value: any) => {
    if (key === 'tierLevel') {
      const tierLevel = value === '' || value == null ? null : Number(value)
      if (tierLevel !== null && !Number.isFinite(tierLevel)) return
      updateLoanProcess({ key: 'tierLevel', value: tierLevel }).then((res) => {
        if (!res?.success) return
        dispatch(
          setLoanDetail({
            rateData: { ...loanDetail.rateData, tierLevel },
          }),
        )
      })
      return
    }
    if (key === 'allowCashOutPercentage') {
      value = Number(value)
      if (value < 0 || value > 100) return
    }
    const json: Record<string, any> = { [key]: value }
    if (key === 'allowCashOut') json['allowCashOutPercentage'] = 0
    updateLoanFields(json).then(() => {
      dispatch(setLoanGroupData(json))
    })
  }

  const renderValue = (value: React.ReactNode) => {
    if (value === 'ALLOW_CASHOUT') {
      // Not every loan carries a cash-out setting. Without the default option the select would
      // show the first choice ("Yes") for a loan that has none stored.
      if (!canEditLoanDetailsSideBar) return <span>{AllowCashOutTypeText[loan.allowCashOut] || 'N/A'}</span>
      return (
        <Select
          id="cash-out-reserves"
          title=""
          size={3}
          className="-mb-4"
          options={AllowCashOutTypeText}
          value={loan.allowCashOut?.toString() || ''}
          onChange={(v) => onFieldChange('allowCashOut', v)}
          hasDefaultOption
          defaultOptionText="N/A"
        />
      )
    }
    if (value === 'PERCENTAGE') {
      return (
        <div className="flex items-center gap-2">
          <div className="[&>*:first-child]:mb-0">
            {canEditLoanDetailsSideBar ? (
              <Input
                name="cash-out-percentage"
                title=""
                type="number"
                value={loan.allowCashOutPercentage}
                onChange={(v) => onFieldChange('allowCashOutPercentage', v)}
                className="w-24"
                placeholder="Enter %"
              />
            ) : (
              <span>{loan.allowCashOutPercentage}</span>
            )}
          </div>
          <span className="text-sm text-gray-600">%</span>
        </div>
      )
    }
    if (typeof value == 'string') {
      return <div dangerouslySetInnerHTML={{ __html: value }} />
    }
    return value
  }

  return (
    <>
      <FloaterTabWrapper floater={floater}>
        <button
          className={`p-3 ${floaterTabEdgeClass(
            floater.side,
          )} ring-[1px] ring-shade-blue bg-shade-blue text-white border hover:bg-white border-shade-blue hover:text-shade-blue rounded flex items-center gap-2 w-auto sm:w-[155px]`}
          {...floater.tabProps(onShowSlide)}
        >
          <DocumentTextIcon className="w-6 h-6" />
          <span className="hidden sm:inline">Loan Details</span>
        </button>
      </FloaterTabWrapper>
      {isShowSlide && (
        <div
          className={`z-50 shadow1 fixed bg-white border top-[5vh] bottom-0 w-[24rem] max-w-full h-[90vh] overflow-auto ease-in-out ${floaterPanelEdgeClass(
            floater.side,
          )}`}
        >
          <div className="w-[24rem] max-w-full fixed z-20 rounded-t flex justify-between p-3 font-bold text-sm place-items-center items-center text-shade-blue border-b bg-white">
            <div className="flex items-center">
              <p className="text-lg text-gray-800">Loan Details: </p>
              <span className="text-[16px] ml-2 italic font-semibold">{getOpenLoanDisplayNo(loanDetail)}</span>
            </div>
            <button className="hover:underline hover-shadow1 p-1 rounded" onClick={onShowSlide}>
              {floater.side === 'left' ? (
                <ArrowLeftCircleIcon className="w-5 h-5" />
              ) : (
                <ArrowRightCircleIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="pt-14 h-fit overflow-auto relative text-sm">
            {displaySections.map((section) => (
              <div key={section.id}>
                <p className="text-shade-blue bg-blue-200 py-1 px-3 border-b font-bold">- {section.title}</p>
                <table className="w-full">
                  <tbody>
                    {section.fields.map((field, index) => (
                      <tr key={field.key} className={`${index % 2 && 'bg-gray-50'}`}>
                        <td className="py-1 px-2 border-b">{field.title}:</td>
                        <td className="font-variation-settings-600 border-b text-left py-1 pr-2">
                          {renderValue(field.value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
